import { useState, type ReactNode } from 'react'

interface Props {
  title: string
  count?: number
  defaultOpen?: boolean
  /** Força a secção aberta (ex.: durante uma pesquisa) sem alterar a
   *  preferência guardada do utilizador. */
  forceOpen?: boolean
  children: ReactNode
}

// LocalStorage key for a section's expand state. Keyed by the title
// so the user's preference for e.g. "Relações inversas" survives
// switching selection (which remounts the panel) and even page
// reloads. Defaults are only applied the first time the user sees
// the section; once they've toggled it, that wins for ever after.
const STORAGE_PREFIX = 'vn2r:section:'

function storageKey(title: string): string {
  return STORAGE_PREFIX + title
}

function readStored(title: string): boolean | null {
  try {
    const v = localStorage.getItem(storageKey(title))
    if (v === '1') return true
    if (v === '0') return false
    return null
  } catch {
    return null
  }
}

function writeStored(title: string, open: boolean): void {
  try {
    localStorage.setItem(storageKey(title), open ? '1' : '0')
  } catch {
    /* ignore, quota / private mode */
  }
}

function Section({
  title,
  count,
  defaultOpen = true,
  forceOpen = false,
  children,
}: Props) {
  const [open, setOpen] = useState<boolean>(() => {
    const stored = readStored(title)
    return stored ?? defaultOpen
  })
  const toggle = () => {
    setOpen((prev) => {
      const next = !prev
      writeStored(title, next)
      return next
    })
  }
  const effectiveOpen = forceOpen || open
  return (
    <section className="border-b border-[var(--color-border)]">
      <button
        type="button"
        onClick={toggle}
        className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
      >
        <span>
          {title}
          {typeof count === 'number' && (
            <span className="ml-2 rounded bg-[var(--color-bg)] px-1.5 py-0.5 text-[10px] text-[var(--color-text-muted)]">
              {count}
            </span>
          )}
        </span>
        <span className="text-[var(--color-text-muted)]">
          {effectiveOpen ? '−' : '+'}
        </span>
      </button>
      {effectiveOpen && <div className="px-3 pb-3 text-xs">{children}</div>}
    </section>
  )
}

export default Section
